import React from 'react';

import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/froala_style.min.css';
import 'tributejs/dist/tribute.css';

import 'froala-editor/js/plugins.pkgd.min.js';

import 'froala-editor/js/languages/de.js';
import 'froala-editor/js/languages/en_gb.js';
import 'froala-editor/js/languages/ru.js';
import './entities/lang/tr';

import FroalaEditor from 'react-froala-wysiwyg';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';

import Froalaeditor from 'froala-editor';

import {
	createGuid,
	debounce,
	extend,
	IDictionary,
	ILanguage,
	LicenseKeys,
	MLHelper
} from '@bimser/core';
import IconLanguage from '@bimser/icons/16/language';
import classNames from 'classnames/bind';
import Tribute from 'tributejs';
import { IEditorProps } from '.';
import { BCCombobox, findMessage, IComboboxSelectedChangeEventArgs } from '..';
import { EditorHelpSet } from './entities/EditorHelpSet';
import EditorToolbar from './entities/EditorToolbar';
import { enabledPlugins } from './entities/EnabledPlugins';
import IEditorToolbar from './entities/IEditorToolbar';

import Hash from 'object-hash';

import Styles from './assets/styles.scss';

const cx = classNames.bind(Styles);

const Editor = React.forwardRef<BCEditorRef, IEditorProps>(function Editor(props, ref) {
	const guid = React.useRef<string>(createGuid());
	const [value, setValue] = React.useState<IDictionary<string>>(
		typeof props.value === 'string' ? { system: props.value } : props.value
	);
	const [editor, setEditor] = React.useState<any>(null);
	const [selectedLanguage, setSelectedLanguage] = React.useState<Partial<ILanguage>>(
		props.enableMultilanguage ? MLHelper.currentLanguage : { culture: 'system' }
	);
	const [printing, setPrinting] = React.useState(false);
	const froalaRef = React.useRef<FroalaEditor>();

	React.useImperativeHandle(ref, () => ({
		focus: () => editor?.events.focus(),
		froalaRef: froalaRef.current,
		setPrinting,
		editor
	}));

	React.useEffect(() => {
		if (editor) {
			editor.opts.placeholderText = props.placeHolder;
			editor.placeholder.refresh();
		}
	}, [props.placeHolder]);

	React.useEffect(() => {
		setValue(typeof props.value === 'string' ? { system: props.value } : props.value);
	}, [props.value]);

	const debouncedUpdateDimensions = debounce(() => {
		editor?.size.syncIframe();
	}, 500);

	React.useEffect(() => {
		if (props.iframe) {
			debouncedUpdateDimensions();
		}
	}, [editor, value]);

	React.useEffect(() => {
		if (props.iframe) {
			window.addEventListener('resize', debouncedUpdateDimensions);
		}
		return () => {
			window.removeEventListener('resize', debouncedUpdateDimensions);
		};
	}, [editor]);

	React.useEffect(() => {
		if (props.readOnly) {
			editor?.edit.off();
		} else {
			editor?.edit.on();
		}
	}, [props.readOnly, selectedLanguage]);

	React.useLayoutEffect(() => {
		registerCustomLinkButton();
	}, []);

	const filterButtons = (toolbarButtons: IEditorToolbar) => {
		if (!props.noFullscreen) {
			return toolbarButtons;
		}

		const newToolbarButtons = extend(true, {}, toolbarButtons);

		newToolbarButtons.moreMisc.buttons = newToolbarButtons.moreMisc.buttons.filter(button => {
			if (button === 'fullscreen' && props.noFullscreen) {
				return false;
			}

			return true;
		});

		return newToolbarButtons;
	};

	const editorToolbar = React.useMemo(() => {
		switch (props.toolbarType) {
			case 'custom':
				return props.toolbarButtons || {};
			case 'rich':
				return filterButtons(EditorToolbar.RichToolbar);
			case 'standard':
				return filterButtons(EditorToolbar.StandardToolbar);
			case 'basic':
			default:
				return filterButtons(EditorToolbar.BasicToolbar);
		}
	}, [props.toolbarButtons, props.toolbarType]);

	const onValueChange = (newModel: string) => {
		const oldModel = value?.[selectedLanguage.culture];

		if (oldModel !== newModel) {
			setValue({
				...value,
				[selectedLanguage.culture]: newModel
			});

			props.onValueChange?.(selectedLanguage.culture, newModel);
		}
	};

	const registerCustomLinkButton = () => {
		// https://github.com/froala/wysiwyg-editor/issues/3818 - Please review for future updates
		Froalaeditor.DefineIcon('linkOpen', {
			NAME: 'external-link',
			FA5NAME: 'external-link-alt',
			SVG_KEY: 'openLink'
		});

		Froalaeditor.RegisterCommand('linkOpen', {
			title: 'Open Link',
			icon: 'linkOpen',
			refresh($btn) {
				const link = this.link.get();
				$btn.toggleClass('fr-hidden', !link);
			},
			callback() {
				const link = this.link.get() as HTMLAnchorElement;

				if (link) {
					if (link.href.indexOf('mailto:') !== -1) {
						window.open(link.href).close();
					} else {
						window.open(link.href, '_blank');
					}

					this.popups.hide('link.edit');
					props.onLinkClick?.(link);
				}
			}
		});
	};

	const onInitialized = function () {
		const editor = this;
		if (props.tributeObjects?.length) {
			props.tributeObjects.forEach(tribute => {
				tribute.attach(editor.el);
			});
			editor.events.on(
				'keydown',
				function (e: any) {
					const anyActiveTribute = props.tributeObjects.findIndex(t => t.isActive) > -1;
					if (e.which == 13 && anyActiveTribute) {
						return false;
					}
				},
				true
			);
		} else {
			editor.events.on(
				'keydown',
				function (e: any) {
					if (e.which == 8) {
						return false;
					}
				},
				true
			);
		}
		setEditor(editor);
		if (props.readOnly) {
			editor.edit.off();
		}
	};

	const config = {
		key: LicenseKeys.FROALA_LICENCE_KEY,
		attribution: false,
		events: {
			initialized: onInitialized
		},
		placeholderText: typeof props.placeHolder === 'string' ? props.placeHolder : null,
		heightMin: props.minHeight,
		heightMax: props.maxHeight,
		height: props.height,
		toolbarButtons: editorToolbar,
		toolbarBottom: props.toolbarBottom,
		language: MLHelper.currentLanguage.twoLetterCulture,
		linkInsertButtons: [],
		charCounterCount: !!props.charCounterMax || props.charCounterCount,
		charCounterMax: props.charCounterMax || -1,
		scrollableContainer: props.scrollableContainer || 'body',
		zIndex: props.zIndex || 1,
		linkStyles: {
			'fr-strong': findMessage.get('102423')
		},
		paragraphStyles: {
			'fr-text-bordered': findMessage.get('102416'),
			'fr-text-spaced': findMessage.get('102417'),
			'fr-text-uppercase': findMessage.get('102418')
		},
		imageStyles: {
			'fr-bordered': findMessage.get('102416'),
			'fr-shadow': findMessage.get('102419')
		},
		helpSets: EditorHelpSet,
		pluginsEnabled: enabledPlugins,
		toolbarSticky: props.toolbarSticky ?? true,
		linkAlwaysBlank: true,
		emoticonsUseImage: false,
		style: props.style,
		quickInsertEnabled: false,
		iframe: !printing && props.iframe,
		iframeStyle: `
					body {
					  font-family: sans-serif;
					  color: #414141;
					  font-size: 14px;
					  line-height: 1.6;
					}
				  `
	};

	const renderEditor = () => {
		if (props.viewerMode || props.disabled) {
			return <FroalaEditorView model={value[selectedLanguage.culture]} />;
		} else {
			const key = Hash({ ...config, guid: guid.current });
			return (
				<FroalaEditor
					key={key}
					ref={froalaRef}
					tag="textarea"
					config={config}
					model={value[selectedLanguage.culture] || ''}
					onModelChange={onValueChange}
				/>
			);
		}
	};

	const selectedLanguageChange = (args: IComboboxSelectedChangeEventArgs) => {
		editor.edit.off();
		setSelectedLanguage(
			MLHelper.supportedLanguages.find(l => l.culture === (args.data as string))
		);
	};

	const renderLanguageSelector = () => {
		return (
			<div className={Styles.languageSelector}>
				<IconLanguage
					width="24px"
					height="24px"
				/>
				<BCCombobox
					options={MLHelper.supportedLanguages.map(lang => ({
						key: lang.culture,
						value: lang.culture,
						text: lang.name
					}))}
					style={{ width: '120px' }}
					onSelectedChange={selectedLanguageChange}
					value={selectedLanguage.culture}
					size="small"
				/>
			</div>
		);
	};

	const className = cx({
		htmlTextBoxStyles: true,
		multiLanguageEnabled: props.enableMultilanguage,
		[props.className]: !!props.className
	});

	return (
		<div
			className={className}
			style={props.style}
		>
			{props.enableMultilanguage && renderLanguageSelector()}
			{renderEditor()}
		</div>
	);
});

type BCEditorRef = {
	focus: () => void;
	froalaRef: FroalaEditor;
	editor: any;
	setPrinting: React.Dispatch<React.SetStateAction<boolean>>;
};
export default Editor;
export { BCEditorRef, Tribute };
